import { createSlice, createSelector, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/app/types';
import type {
  AddOnProduct,
  CompanyWideSelection,
  PerSeatSelection,
  CheckoutAddOnsState,
} from '../types/checkoutAddOns';

// ============================================================================
// Initial State
// ============================================================================

const initialState: CheckoutAddOnsState = {
  eventId: null,
  availableAddOns: [],
  companyWideSelections: {},
  perSeatSelections: {},
  isLoading: false,
  error: null,
};

// ============================================================================
// Redux Slice
// ============================================================================

const checkoutAddOnsSlice = createSlice({
  name: 'checkoutAddOns',
  initialState,
  reducers: {
    // Initialize checkout with event add-ons
    setEventAddOns(
      state,
      action: PayloadAction<{ eventId: string; addOnProducts: AddOnProduct[] }>
    ) {
      const { eventId, addOnProducts } = action.payload;
      state.eventId = eventId;
      state.availableAddOns = addOnProducts;

      // Auto-select 'included' company-wide add-ons
      state.companyWideSelections = {};
      addOnProducts.forEach((addon) => {
        if (
          addon.availabilityStatus === 'included' &&
          addon.purchaseScope === 'company_wide'
        ) {
          state.companyWideSelections[addon.id] = {
            addOnProductId: addon.id,
            availabilityStatus: 'included',
            price: 0,
            configValues: null,
          };
        }
      });

      state.isLoading = false;
      state.error = null;
    },

    // Toggle company-wide add-on selection
    toggleCompanyWideAddOn(
      state,
      action: PayloadAction<{ addOnProductId: string; price: number }>
    ) {
      const { addOnProductId, price } = action.payload;

      if (state.companyWideSelections[addOnProductId]) {
        // Only allow deselection if not 'included'
        const selection = state.companyWideSelections[addOnProductId];
        if (selection.availabilityStatus !== 'included') {
          delete state.companyWideSelections[addOnProductId];
        }
      } else {
        state.companyWideSelections[addOnProductId] = {
          addOnProductId,
          availabilityStatus: 'available',
          price,
          configValues: null,
        };
      }
    },

    // Toggle attendee assignment for per-seat add-on
    togglePerSeatAttendee(
      state,
      action: PayloadAction<{
        addOnProductId: string;
        attendeeId: string;
        unitPrice: number;
      }>
    ) {
      const { addOnProductId, attendeeId, unitPrice } = action.payload;

      if (!state.perSeatSelections[addOnProductId]) {
        // Create new per-seat selection
        state.perSeatSelections[addOnProductId] = {
          addOnProductId,
          attendeeIds: [attendeeId],
          unitPrice,
          totalPrice: unitPrice,
          configValues: null,
        };
      } else {
        // Update existing per-seat selection
        const selection = state.perSeatSelections[addOnProductId];
        const attendeeIndex = selection.attendeeIds.indexOf(attendeeId);

        if (attendeeIndex >= 0) {
          // Remove attendee
          selection.attendeeIds.splice(attendeeIndex, 1);

          // Remove selection if no attendees left
          if (selection.attendeeIds.length === 0) {
            delete state.perSeatSelections[addOnProductId];
          } else {
            selection.totalPrice = selection.unitPrice * selection.attendeeIds.length;
          }
        } else {
          // Add attendee (enforce uniqueness constraint from clarification Q1)
          if (!selection.attendeeIds.includes(attendeeId)) {
            selection.attendeeIds.push(attendeeId);
            selection.totalPrice = selection.unitPrice * selection.attendeeIds.length;
          }
        }
      }
    },

    // Remove attendee from all per-seat selections
    removeAttendeeFromAllPerSeatAddOns(
      state,
      action: PayloadAction<{ attendeeId: string }>
    ) {
      const { attendeeId } = action.payload;

      Object.keys(state.perSeatSelections).forEach((addOnProductId) => {
        const selection = state.perSeatSelections[addOnProductId];
        const attendeeIndex = selection.attendeeIds.indexOf(attendeeId);

        if (attendeeIndex >= 0) {
          selection.attendeeIds.splice(attendeeIndex, 1);

          if (selection.attendeeIds.length === 0) {
            delete state.perSeatSelections[addOnProductId];
          } else {
            selection.totalPrice = selection.unitPrice * selection.attendeeIds.length;
          }
        }
      });
    },

    // Set loading state
    setLoading(state, action: PayloadAction<boolean>) {
      state.isLoading = action.payload;
    },

    // Set error state
    setError(state, action: PayloadAction<string | null>) {
      state.error = action.payload;
      state.isLoading = false;
    },

    // Clear checkout add-ons state
    clearCheckoutAddOns() {
      return initialState;
    },
  },
});

// ============================================================================
// Basic Selectors
// ============================================================================

export const selectAvailableAddOns = (state: RootState) =>
  state.checkoutAddOns.availableAddOns;

export const selectCompanyWideSelections = (state: RootState) =>
  state.checkoutAddOns.companyWideSelections;

export const selectPerSeatSelections = (state: RootState) =>
  state.checkoutAddOns.perSeatSelections;

export const selectIsLoading = (state: RootState) =>
  state.checkoutAddOns.isLoading;

export const selectError = (state: RootState) =>
  state.checkoutAddOns.error;

// ============================================================================
// Memoized Selectors
// ============================================================================

// Calculate total company-wide cost
const selectCompanyWideTotal = createSelector(
  [selectCompanyWideSelections],
  (selections) => {
    return Object.values(selections).reduce((sum, s) => sum + s.price, 0);
  }
);

// Calculate total per-seat cost
const selectPerSeatTotal = createSelector(
  [selectPerSeatSelections],
  (selections) => {
    return Object.values(selections).reduce((sum, s) => sum + s.totalPrice, 0);
  }
);

// Calculate total add-on cost (company-wide + per-seat)
export const selectTotalAddOnCost = createSelector(
  [selectCompanyWideTotal, selectPerSeatTotal],
  (companyWideTotal, perSeatTotal) => companyWideTotal + perSeatTotal
);

// Check if attendee has per-seat add-ons assigned
export const selectAttendeeHasPerSeatAddOns = (attendeeId: string) =>
  createSelector([selectPerSeatSelections], (perSeat) => {
    return Object.values(perSeat).some((s) => s.attendeeIds.includes(attendeeId));
  });

// ============================================================================
// Actions and Reducer Export
// ============================================================================

export const {
  setEventAddOns,
  toggleCompanyWideAddOn,
  togglePerSeatAttendee,
  removeAttendeeFromAllPerSeatAddOns,
  setLoading,
  setError,
  clearCheckoutAddOns,
} = checkoutAddOnsSlice.actions;

export default checkoutAddOnsSlice.reducer;
